import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Coins, TrendingUp, TrendingDown, ArrowDownToLine, ArrowUpToLine,
    Lock, Plus, RefreshCcw, Pencil, Trash2, ArrowLeftRight,
    LayoutDashboard, ListChecks, Wallet, ScrollText, Calculator,
    CalendarClock, BarChart3, AlertCircle, Printer,
} from "lucide-react";
import MobileCashFab from "@/components/MobileCashFab";

/* ═══════════════════════════════════════════════════════════════════
   CASH DESK — full-featured module hub
   Tabs: Dashboard · Accounts · Ledger · Transfers · Reconciliation
         · Monthly Closing · Reports
   ═══════════════════════════════════════════════════════════════════ */

const TABS = [
    { key: "dashboard",      label: "Dashboard",       icon: LayoutDashboard },
    { key: "accounts",       label: "Accounts",        icon: Wallet },
    { key: "ledger",         label: "Ledger",          icon: ScrollText },
    { key: "transfers",      label: "Transfers",       icon: ArrowLeftRight },
    { key: "reconciliation", label: "Weekly Recon",    icon: Calculator },
    { key: "closing",        label: "Monthly Closing", icon: CalendarClock },
    { key: "reports",        label: "Reports",         icon: BarChart3 },
];

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function CashDesk() {
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") || "dashboard";
    const setTab = (t) => setParams({ tab: t });

    const [accounts, setAccounts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [banks, setBanks] = useState([]);
    // Deep-link support — /cash-desk?tab=ledger&add=expense opens the entry
    // dialog directly so Quick-Add and ⌘K can jump straight to a cash entry
    // without the user hunting for the tab + button. Amount / note prefill
    // via ?amount= and ?note= are also honoured by LedgerPanel.
    const addKind = params.get("add");
    const prefill = {
        amount: params.get("amount") || "",
        note: params.get("note") || "",
    };

    const loadCore = useCallback(async () => {
        try {
            const [a, s, b] = await Promise.all([
                api.get("/cash/accounts"),
                api.get("/cash/summary"),
                api.get("/bank-accounts").catch(() => ({ data: [] })),
            ]);
            setAccounts(a.data?.accounts || []);
            setSummary(s.data);
            setBanks(b.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);
    useEffect(() => { loadCore(); }, [loadCore]);

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="cash-desk-page">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="eyebrow">Cash Management</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2 dark:text-white">
                        Cash Desk.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Every rupee in and out — Petty · Service · Personal cash accounts, transfers,
                        weekly reconciliation, monthly closing and account-wise reports.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={loadCore} data-testid="cash-refresh">
                    <RefreshCcw size={13} className="mr-1.5" /> Refresh
                </Button>
            </div>

            {/* Tab pills */}
            <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="cash-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition ${
                                active
                                    ? "bg-stone-900 text-white border-stone-900"
                                    : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
                            }`}
                            data-testid={`cash-tab-${t.key}`}
                        >
                            <Icon size={13} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Panels */}
            {tab === "dashboard"      && <DashboardPanel summary={summary} accounts={accounts} onReload={loadCore} />}
            {tab === "accounts"       && <AccountsPanel  accounts={accounts} onReload={loadCore} />}
            {tab === "ledger"         && <LedgerPanel    accounts={accounts} banks={banks} onReload={loadCore} autoOpenKind={addKind} prefill={prefill} onDialogHandled={() => { const p = new URLSearchParams(params); p.delete("add"); p.delete("amount"); p.delete("note"); setParams(p); }} />}
            {tab === "transfers"      && <TransfersPanel accounts={accounts} onReload={loadCore} />}
            {tab === "reconciliation" && <ReconPanel     accounts={accounts} />}
            {tab === "closing"        && <ClosingPanel   accounts={accounts} />}
            {tab === "reports"        && <ReportsPanel />}
            {/* Mobile-only floating action button for one-thumb cash logging */}
            <MobileCashFab />
        </div>
    );
}

/* ═══════════ Dashboard ═══════════ */
function DashboardPanel({ summary, accounts }) {
    return (
        <>
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="cash-summary">
                    <Kpi icon={Coins}          label="Available cash"     value={fmtMoney(summary.available_balance)} tone="stone" testId="cash-balance" />
                    <Kpi icon={TrendingUp}     label="Today's sales"      value={fmtMoney(summary.today_sales)}       tone="emerald" />
                    <Kpi icon={TrendingDown}   label="Today's expenses"   value={fmtMoney(summary.today_expenses)}    tone="rose" />
                    <Kpi icon={ArrowDownToLine} label="Deposited (month)" value={fmtMoney(summary.month_deposits)}    tone="blue" />
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="cash-accounts-strip">
                {accounts.length === 0 ? (
                    <div className="col-span-full text-sm text-stone-500 border border-dashed border-stone-200 rounded-2xl p-6 text-center">
                        No cash accounts yet.
                    </div>
                ) : accounts.map((a) => (
                    <div key={a.id} className="bg-white border border-stone-200 rounded-2xl p-4 dark:bg-stone-900 dark:border-stone-800" data-testid={`cash-account-card-${a.type.replace(/\s+/g, "-").toLowerCase()}`}>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-6 h-6 rounded bg-stone-100 flex items-center justify-center"><Coins size={12} /></span>
                            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500">{a.name}</div>
                        </div>
                        <div className="text-2xl font-bold tabular-nums text-stone-900 dark:text-white">{fmtMoney(a.current_balance)}</div>
                        <div className="text-[11px] text-stone-500 mt-1">
                            {a.last_reconciled ? `Last recon: ${a.last_reconciled}` : "Not reconciled yet"} · {a.last_recon_status}
                        </div>
                    </div>
                ))}
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-4">
                <div className="eyebrow mb-2">Quick help</div>
                <ul className="text-sm text-stone-600 space-y-1">
                    <li>· Use <b>Ledger</b> to record any cash sale/expense/deposit/withdrawal with Edit &amp; Delete controls.</li>
                    <li>· Use <b>Transfers</b> for movements between Petty · Service · Personal accounts.</li>
                    <li>· Do a <b>Weekly Reconciliation</b> every Monday — physical vs expected balance.</li>
                    <li>· Do a <b>Monthly Closing</b> after all weekly recons are done — it locks the month.</li>
                </ul>
            </div>
        </>
    );
}

function Kpi({ icon: Icon, label, value, tone, testId }) {
    const tones = {
        emerald: "text-emerald-700 bg-emerald-50", rose: "text-rose-700 bg-rose-50",
        blue: "text-blue-700 bg-blue-50", stone: "text-stone-700 bg-stone-100",
    };
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-4 dark:bg-stone-900 dark:border-stone-800" data-testid={testId}>
            <div className="flex items-center gap-2 mb-1">
                <span className={`w-6 h-6 rounded flex items-center justify-center ${tones[tone] || tones.stone}`}><Icon size={12} /></span>
                <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500">{label}</div>
            </div>
            <div className="text-2xl font-bold tabular-nums text-stone-900 dark:text-white">{value}</div>
        </div>
    );
}

/* ═══════════ Accounts ═══════════ */
function AccountsPanel({ accounts, onReload }) {
    const [dialog, setDialog] = useState(null);
    const save = async (payload, id) => {
        try {
            if (id) await api.put(`/cash/accounts/${id}`, payload);
            else await api.post("/cash/accounts", payload);
            toast.success(id ? "Account updated" : "Account created");
            setDialog(null);
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const remove = async (a) => {
        if (!window.confirm(`Deactivate/Delete ${a.name}?`)) return;
        try {
            const res = await api.delete(`/cash/accounts/${a.id}`);
            toast.success(res.data.deactivated ? "Account deactivated (had entries)" : "Account deleted");
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-stone-500">Manage your Petty / Service / Personal (or custom) cash accounts.</div>
                <Button size="sm" onClick={() => setDialog({})} data-testid="cash-account-new"><Plus size={13} className="mr-1" /> New account</Button>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="cash-accounts-table">
                <table className="dense-table w-full">
                    <thead>
                        <tr><th>Name</th><th>Type</th><th>Opening</th><th>Current balance</th><th>Last reconciled</th><th>Status</th><th className="text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {accounts.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-stone-400 py-8">No cash accounts. Create one to start.</td></tr>
                        ) : accounts.map((a) => (
                            <tr key={a.id} data-testid={`cash-account-row-${a.id}`}>
                                <td className="font-medium">{a.name}</td>
                                <td>{a.type}</td>
                                <td className="tabular-nums">{fmtMoney(a.opening_balance)}</td>
                                <td className="tabular-nums font-semibold">{fmtMoney(a.current_balance)}</td>
                                <td className="text-xs">{a.last_reconciled || "—"}</td>
                                <td>
                                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                                        a.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"
                                    }`}>{a.status || "Active"}</span>
                                </td>
                                <td className="text-right">
                                    <button className="text-stone-500 hover:text-stone-900 mr-3" onClick={() => setDialog(a)} data-testid={`cash-account-edit-${a.id}`}><Pencil size={13} /></button>
                                    <button className="text-rose-500 hover:text-rose-700" onClick={() => remove(a)} data-testid={`cash-account-delete-${a.id}`}><Trash2 size={13} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {dialog && <AccountDialog init={dialog} onClose={() => setDialog(null)} onSave={save} />}
        </>
    );
}

function AccountDialog({ init, onClose, onSave }) {
    const isEdit = !!init.id;
    const [f, setF] = useState({
        name: init.name || "",
        type: init.type || "Petty Cash",
        opening_balance: init.opening_balance ?? 0,
        owner_name: init.owner_name || "",
        approval_above: init.approval_above ?? 10000,
        monthly_carry_forward: init.monthly_carry_forward ?? true,
        remarks: init.remarks || "",
    });
    const submit = () => {
        if (!f.name || f.name.length < 2) { toast.error("Enter account name"); return; }
        onSave({ ...f, opening_balance: Number(f.opening_balance) || 0, approval_above: Number(f.approval_above) || 0 }, init.id);
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="cash-account-dialog">
                <DialogHeader><DialogTitle>{isEdit ? "Edit account" : "New cash account"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label className="eyebrow">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="acc-name" /></div>
                    <div><Label className="eyebrow">Type</Label>
                        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                            <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Petty Cash">Petty Cash</SelectItem>
                                <SelectItem value="Service Cash">Service Cash</SelectItem>
                                <SelectItem value="Personal">Personal</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label className="eyebrow">Owner</Label><Input value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} /></div>
                    {!isEdit && <div><Label className="eyebrow">Opening balance (₹)</Label><Input type="number" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: e.target.value })} data-testid="acc-opening" /></div>}
                    <div><Label className="eyebrow">Approval above (₹)</Label><Input type="number" value={f.approval_above} onChange={(e) => setF({ ...f, approval_above: e.target.value })} /></div>
                    <div className="col-span-2"><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-stone-900 text-white" onClick={submit} data-testid="acc-save">{isEdit ? "Save" : "Create"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ═══════════ Bulk Voucher Print ═══════════ */
/**
 * Print every voucher for a chosen day (or short range) as one A5 page-
 * broken document. Opens the bulk endpoint in a new tab; the endpoint
 * triggers `window.print()` itself so the shopkeeper only needs one tap.
 */
function BulkVoucherPrintButton() {
    const [open, setOpen] = useState(false);
    const [from, setFrom] = useState(today());
    const [to, setTo] = useState(today());
    return (
        <>
            <Button
                size="sm" variant="outline" onClick={() => setOpen(true)}
                data-testid="ledger-bulk-voucher"
                title="Print all vouchers for a chosen day / range"
            >
                <Printer size={13} className="mr-1" /> Print day
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="bulk-voucher-dialog"
                    className="max-w-md h-auto p-5 sm:p-6"
                >
                    <DialogHeader>
                        <DialogTitle className="text-lg">Print vouchers</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-stone-500 -mt-2 mb-2">
                        Every sale, expense, deposit and withdrawal voucher for the range you choose will print as one A5 stack. Transfers are excluded.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="eyebrow">From</Label>
                            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="bulk-voucher-from" />
                        </div>
                        <div>
                            <Label className="eyebrow">To</Label>
                            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="bulk-voucher-to" />
                        </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row-reverse gap-2 sm:gap-0 mt-4">
                        <Button
                            className="bg-stone-900 text-white w-full sm:w-auto"
                            data-testid="bulk-voucher-print-btn"
                            onClick={() => {
                                const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/cash/vouchers/bulk?date_from=${from}&date_to=${to}`;
                                window.open(url, "_blank", "noopener,noreferrer");
                                setOpen(false);
                            }}
                        >
                            <Printer size={13} className="mr-1" /> Print stack
                        </Button>
                        <Button variant="outline" className="w-full sm:w-auto" onClick={() => setOpen(false)}>Cancel</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/* ═══════════ Ledger with CRUD ═══════════ */
function LedgerPanel({ accounts, banks, onReload, autoOpenKind, prefill, onDialogHandled }) {
    const [rows, setRows] = useState([]);
    const [accFilter, setAccFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [dialog, setDialog] = useState(null); // { mode: "new"/"edit", kind, row? }

    // Auto-open the entry dialog when the URL requests it — used by Quick Add
    // "Add cash entry" and ⌘K "Cash sale / Cash expense" deep-links.
    useEffect(() => {
        const validKinds = ["sale", "expense", "deposit", "withdrawal"];
        if (autoOpenKind && validKinds.includes(autoOpenKind) && !dialog) {
            setDialog({ mode: "new", kind: autoOpenKind, prefill });
            onDialogHandled?.();
        }
    }, [autoOpenKind, dialog, prefill, onDialogHandled]);

    const load = useCallback(async () => {
        try {
            const params = { limit: 500 };
            if (accFilter) params.account_id = accFilter;
            if (typeFilter) params.txn_type = typeFilter;
            const r = await api.get("/cash/ledger", { params });
            setRows(r.data?.ledger || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, [accFilter, typeFilter]);
    useEffect(() => { load(); }, [load]);

    const submit = async ({ kind, payload, id }) => {
        try {
            if (id) {
                await api.put(`/cash/transaction/${id}`, payload);
                toast.success("Entry updated");
            } else {
                await api.post(`/cash/${kind}`, payload);
                toast.success(`Cash ${kind} recorded`);
            }
            setDialog(null);
            load();
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete ${row.type} ${fmtMoney(row.amount)} · ${row.voucher_number}?`)) return;
        try {
            await api.delete(`/cash/transaction/${row.id}`);
            toast.success("Entry deleted");
            load();
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const openNew = (kind) => setDialog({ mode: "new", kind });
    const openEdit = (row) => setDialog({ mode: "edit", kind: row.type, row });

    return (
        <>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={accFilter || "all"} onValueChange={(v) => setAccFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-[180px]" data-testid="ledger-acc-filter"><SelectValue placeholder="All accounts" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All accounts</SelectItem>
                            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-[160px]" data-testid="ledger-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            <SelectItem value="sale">Sale</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                            <SelectItem value="deposit">Deposit</SelectItem>
                            <SelectItem value="withdrawal">Withdrawal</SelectItem>
                            <SelectItem value="transfer_in">Transfer in</SelectItem>
                            <SelectItem value="transfer_out">Transfer out</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openNew("sale")} data-testid="ledger-new-sale"><Plus size={13} className="mr-1" />Sale</Button>
                    <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => openNew("expense")} data-testid="ledger-new-expense"><Plus size={13} className="mr-1" />Expense</Button>
                    <Button size="sm" onClick={() => openNew("deposit")} data-testid="ledger-new-deposit"><ArrowDownToLine size={13} className="mr-1" />Deposit</Button>
                    <Button size="sm" variant="outline" onClick={() => openNew("withdrawal")} data-testid="ledger-new-withdrawal"><ArrowUpToLine size={13} className="mr-1" />Withdrawal</Button>
                    <BulkVoucherPrintButton />
                </div>
            </div>

            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="cash-ledger-table">
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>Date</th><th>Type</th><th>Account</th><th>Voucher</th><th>Description</th>
                            <th className="text-right">In (₹)</th><th className="text-right">Out (₹)</th>
                            <th className="text-right">Balance</th><th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-stone-400 py-8">No cash entries yet. Click Sale / Expense / Deposit above to add one.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} data-testid={`cash-row-${r.id}`}>
                                <td className="text-xs tabular-nums">{r.date}</td>
                                <td className="capitalize">{r.type.replace("_", " ")}</td>
                                <td className="text-[12px] text-stone-500">{accounts.find((a) => a.id === r.account_id)?.name || "—"}</td>
                                <td className="text-[11px] text-stone-500 font-mono">{r.voucher_number}</td>
                                <td className="max-w-[240px] truncate">{r.customer_name || r.vendor || r.counterparty_account_name || r.remarks || "—"}</td>
                                <td className="text-right tabular-nums text-emerald-700">{r.cash_in ? fmtMoney(r.cash_in) : ""}</td>
                                <td className="text-right tabular-nums text-rose-700">{r.cash_out ? fmtMoney(r.cash_out) : ""}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(r.running_balance)}</td>
                                <td className="text-right whitespace-nowrap">
                                    {["transfer_in", "transfer_out"].includes(r.type) ? (
                                        <button className="text-rose-500 hover:text-rose-700" onClick={() => remove(r)} title="Delete transfer" data-testid={`row-del-${r.id}`}><Trash2 size={13} /></button>
                                    ) : (
                                        <>
                                            <a
                                                href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/cash/transaction/${r.id}/voucher`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-stone-500 hover:text-stone-900 mr-2 inline-block align-middle"
                                                title="Print A5 voucher slip"
                                                data-testid={`row-voucher-${r.id}`}
                                            >
                                                <Printer size={13} />
                                            </a>
                                            <button className="text-stone-500 hover:text-stone-900 mr-2" onClick={() => openEdit(r)} data-testid={`row-edit-${r.id}`}><Pencil size={13} /></button>
                                            <button className="text-rose-500 hover:text-rose-700" onClick={() => remove(r)} data-testid={`row-del-${r.id}`}><Trash2 size={13} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <EntryDialog
                    dialog={dialog}
                    accounts={accounts}
                    banks={banks}
                    onClose={() => setDialog(null)}
                    onSubmit={submit}
                />
            )}
        </>
    );
}

function EntryDialog({ dialog, accounts, banks, onClose, onSubmit }) {
    const isEdit = dialog.mode === "edit";
    const row = dialog.row;
    const kind = dialog.kind;
    const preAmount = dialog.prefill?.amount;
    const preNote = dialog.prefill?.note;
    const [f, setF] = useState(() => isEdit ? {
        date: row.date, amount: row.amount, business_personal: row.business_personal || "Business",
        account_id: row.account_id || "",
        customer_name: row.customer_name || "", invoice_number: row.invoice_number || "",
        product_service: row.product_service || "",
        vendor: row.vendor || "", category: row.category || "", department: row.department || "",
        bank_account_id: row.bank_account_id || "",
        remarks: row.remarks || "",
    } : {
        date: today(), amount: preAmount || "", business_personal: "Business",
        account_id: accounts[0]?.id || "",
        remarks: preNote || "",
        vendor: (kind === "expense" && preNote) ? preNote : "",
        customer_name: (kind === "sale" && preNote) ? preNote : "",
    });
    const submit = async () => {
        if (!f.amount || Number(f.amount) <= 0) { toast.error("Enter an amount"); return; }
        if (!f.account_id) { toast.error("Choose a cash account"); return; }
        const payload = { ...f, amount: Number(f.amount) };
        onSubmit({ kind, payload, id: isEdit ? row.id : null });
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="cash-entry-dialog"
                className="max-w-lg h-[100dvh] sm:h-auto max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-lg p-5 sm:p-6"
            >
                <DialogHeader><DialogTitle className="capitalize text-2xl sm:text-lg">{isEdit ? `Edit ${kind}` : `Cash ${kind}`}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label className="eyebrow">Date</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-11 sm:h-10 text-base sm:text-sm" data-testid="cash-form-date" /></div>
                    <div><Label className="eyebrow">Amount (₹)</Label><Input type="number" inputMode="decimal" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-11 sm:h-10 text-lg sm:text-sm font-mono-figure" data-testid="cash-form-amount" /></div>
                    <div className="col-span-2"><Label className="eyebrow">Cash account</Label>
                        <Select value={f.account_id || ""} onValueChange={(v) => setF({ ...f, account_id: v })}>
                            <SelectTrigger className="h-11 sm:h-10 text-base sm:text-sm" data-testid="cash-form-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                            <SelectContent>
                                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtMoney(a.current_balance)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {kind === "sale" && <>
                        <div className="col-span-2"><Label className="eyebrow">Customer</Label><Input value={f.customer_name || ""} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="cash-form-customer" /></div>
                        <div><Label className="eyebrow">Invoice #</Label><Input value={f.invoice_number || ""} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} /></div>
                        <div><Label className="eyebrow">Product / Service</Label><Input value={f.product_service || ""} onChange={(e) => setF({ ...f, product_service: e.target.value })} /></div>
                    </>}
                    {kind === "expense" && <>
                        <div className="col-span-2"><Label className="eyebrow">Vendor / paid to</Label><Input value={f.vendor || ""} onChange={(e) => setF({ ...f, vendor: e.target.value })} data-testid="cash-form-vendor" /></div>
                        <div><Label className="eyebrow">Category</Label><Input value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Office / Travel / Fuel" /></div>
                        <div><Label className="eyebrow">Department</Label><Input value={f.department || ""} onChange={(e) => setF({ ...f, department: e.target.value })} /></div>
                    </>}
                    {(kind === "deposit" || kind === "withdrawal") && (
                        <div className="col-span-2"><Label className="eyebrow">Bank account</Label>
                            <Select value={f.bank_account_id || ""} onValueChange={(v) => setF({ ...f, bank_account_id: v })}>
                                <SelectTrigger data-testid="cash-form-bank"><SelectValue placeholder="Choose bank" /></SelectTrigger>
                                <SelectContent>
                                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · •••• {b.last_4}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="col-span-2"><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks || ""} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
                </div>
                <DialogFooter className="flex-col sm:flex-row-reverse gap-2 sm:gap-0 mt-2">
                    <Button onClick={submit} className="bg-stone-900 text-white w-full sm:w-auto h-12 sm:h-10 text-base sm:text-sm" data-testid="cash-form-submit">{isEdit ? "Save changes" : "Save"}</Button>
                    <Button variant="outline" onClick={onClose} className="w-full sm:w-auto h-11 sm:h-10">Cancel</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ═══════════ Transfers ═══════════ */
function TransfersPanel({ accounts, onReload }) {
    const [transfers, setTransfers] = useState([]);
    const [dialog, setDialog] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/cash/transfers");
            setTransfers(r.data?.transfers || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (payload) => {
        try {
            await api.post("/cash/transfers", payload);
            toast.success("Transfer recorded");
            setDialog(false);
            load();
            onReload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-stone-500">Move cash between Petty · Service · Personal accounts. Every transfer creates paired ledger entries with a shared voucher.</div>
                <Button size="sm" onClick={() => setDialog(true)} data-testid="cash-transfer-new"><ArrowLeftRight size={13} className="mr-1" /> New transfer</Button>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="cash-transfers-table">
                <table className="dense-table w-full">
                    <thead>
                        <tr><th>Date</th><th>Voucher</th><th>From</th><th>To</th><th className="text-right">Amount</th><th>Reason</th><th>By</th></tr>
                    </thead>
                    <tbody>
                        {transfers.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-stone-400 py-8">No transfers yet.</td></tr>
                        ) : transfers.map((t) => (
                            <tr key={t.id} data-testid={`transfer-row-${t.id}`}>
                                <td className="text-xs tabular-nums">{t.date}</td>
                                <td className="text-[11px] font-mono text-stone-500">{t.voucher_number}</td>
                                <td>{t.from_name}</td>
                                <td>{t.to_name}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(t.amount)}</td>
                                <td className="text-xs text-stone-600 max-w-[240px] truncate">{t.reason || t.remarks || "—"}</td>
                                <td className="text-xs text-stone-500">{t.created_by_name}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {dialog && <TransferDialog accounts={accounts} onClose={() => setDialog(false)} onSubmit={submit} />}
        </>
    );
}

function TransferDialog({ accounts, onClose, onSubmit }) {
    const [f, setF] = useState({ date: today(), from_account_id: "", to_account_id: "", amount: "", reason: "", remarks: "" });
    const submit = () => {
        if (!f.from_account_id || !f.to_account_id) { toast.error("Pick source and destination"); return; }
        if (f.from_account_id === f.to_account_id) { toast.error("Source and destination must differ"); return; }
        if (!f.amount || Number(f.amount) <= 0) { toast.error("Enter amount"); return; }
        onSubmit({ ...f, amount: Number(f.amount) });
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="transfer-dialog">
                <DialogHeader><DialogTitle>New cash transfer</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div><Label className="eyebrow">Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
                    <div><Label className="eyebrow">Amount (₹)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="transfer-amount" /></div>
                    <div><Label className="eyebrow">From</Label>
                        <Select value={f.from_account_id} onValueChange={(v) => setF({ ...f, from_account_id: v })}>
                            <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Source" /></SelectTrigger>
                            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtMoney(a.current_balance)}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label className="eyebrow">To</Label>
                        <Select value={f.to_account_id} onValueChange={(v) => setF({ ...f, to_account_id: v })}>
                            <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Destination" /></SelectTrigger>
                            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-2"><Label className="eyebrow">Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Petty top-up, salary advance, etc." /></div>
                    <div className="col-span-2"><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-stone-900 text-white" onClick={submit} data-testid="transfer-save"><ArrowLeftRight size={13} className="mr-1" />Transfer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ═══════════ Weekly Reconciliation ═══════════ */
function ReconPanel({ accounts }) {
    const [selected, setSelected] = useState(accounts[0]?.id || "");
    const [history, setHistory] = useState([]);
    const [dialog, setDialog] = useState(false);

    useEffect(() => { if (!selected && accounts[0]) setSelected(accounts[0].id); }, [accounts, selected]);

    const load = useCallback(async () => {
        if (!selected) return;
        try {
            const r = await api.get("/cash/reconciliations", { params: { account_id: selected } });
            setHistory(r.data?.reconciliations || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, [selected]);
    useEffect(() => { load(); }, [load]);

    const submit = async (payload) => {
        try {
            const res = await api.post("/cash/reconciliations", payload);
            const diff = res.data?.difference || 0;
            toast.success(Math.abs(diff) < 0.01 ? "Reconciled cleanly" : `Reconciled with difference ${fmtMoney(diff)}`);
            setDialog(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Select value={selected} onValueChange={setSelected}>
                        <SelectTrigger className="w-[220px]" data-testid="recon-account"><SelectValue /></SelectTrigger>
                        <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-sm text-stone-500">Weekly physical vs expected reconciliation.</span>
                </div>
                <Button size="sm" onClick={() => setDialog(true)} disabled={!selected} data-testid="recon-new"><Plus size={13} className="mr-1" /> New reconciliation</Button>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="recon-history-table">
                <table className="dense-table w-full">
                    <thead>
                        <tr><th>Week</th><th className="text-right">Opening</th><th className="text-right">Cash in</th><th className="text-right">Cash out</th><th className="text-right">Expected</th><th className="text-right">Physical</th><th className="text-right">Diff</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {history.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-stone-400 py-8">No reconciliations for this account yet.</td></tr>
                        ) : history.map((r) => (
                            <tr key={r.id} data-testid={`recon-row-${r.id}`}>
                                <td className="text-xs tabular-nums">{r.week_start} → {r.week_end}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.opening_balance)}</td>
                                <td className="text-right tabular-nums text-emerald-700">{fmtMoney(r.cash_in)}</td>
                                <td className="text-right tabular-nums text-rose-700">{fmtMoney(r.cash_out)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.expected_balance)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(r.physical_cash)}</td>
                                <td className={`text-right tabular-nums font-medium ${Math.abs(r.difference || 0) < 0.01 ? "text-emerald-700" : "text-rose-700"}`}>{fmtMoney(r.difference)}</td>
                                <td><span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "Reconciled" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.status}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {dialog && <ReconDialog accountId={selected} accountName={accounts.find((a) => a.id === selected)?.name} onClose={() => setDialog(false)} onSubmit={submit} />}
        </>
    );
}

function ReconDialog({ accountId, accountName, onClose, onSubmit }) {
    const [f, setF] = useState({
        account_id: accountId,
        week_start: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10),
        week_end: today(),
        denominations: { n2000: 0, n500: 0, n200: 0, n100: 0, n50: 0, n20: 0, n10: 0, coins_total: 0 },
        difference_reason: "",
        remarks: "",
    });
    const d = f.denominations;
    const physical = useMemo(() => (
        (d.n2000 || 0) * 2000 + (d.n500 || 0) * 500 + (d.n200 || 0) * 200 + (d.n100 || 0) * 100
        + (d.n50 || 0) * 50 + (d.n20 || 0) * 20 + (d.n10 || 0) * 10 + Number(d.coins_total || 0)
    ), [d]);
    const setDen = (k, v) => setF({ ...f, denominations: { ...d, [k]: Number(v) || 0 } });
    const submit = () => onSubmit({ ...f, physical_cash: physical });

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="recon-dialog">
                <DialogHeader><DialogTitle>Weekly reconciliation · {accountName}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div><Label className="eyebrow">Week start</Label><Input type="date" value={f.week_start} onChange={(e) => setF({ ...f, week_start: e.target.value })} /></div>
                    <div><Label className="eyebrow">Week end</Label><Input type="date" value={f.week_end} onChange={(e) => setF({ ...f, week_end: e.target.value })} /></div>
                </div>
                <div className="eyebrow mb-2">Physical count by denomination</div>
                <div className="grid grid-cols-4 gap-2">
                    {[["n2000", "₹2000"], ["n500", "₹500"], ["n200", "₹200"], ["n100", "₹100"], ["n50", "₹50"], ["n20", "₹20"], ["n10", "₹10"]].map(([k, l]) => (
                        <div key={k}>
                            <Label className="eyebrow text-[10px]">{l}</Label>
                            <Input type="number" value={d[k]} onChange={(e) => setDen(k, e.target.value)} data-testid={`recon-den-${k}`} />
                        </div>
                    ))}
                    <div>
                        <Label className="eyebrow text-[10px]">Coins (₹)</Label>
                        <Input type="number" value={d.coins_total} onChange={(e) => setDen("coins_total", e.target.value)} data-testid="recon-den-coins" />
                    </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 bg-stone-50 rounded-lg p-3">
                    <div><div className="text-[11px] text-stone-500">Physical total</div><div className="font-bold tabular-nums" data-testid="recon-physical">{fmtMoney(physical)}</div></div>
                    <div className="text-[11px] text-stone-500">Expected balance is auto-computed by the system based on this account&apos;s ledger up to week-end.</div>
                </div>
                <Textarea placeholder="Reason if difference exists" value={f.difference_reason} onChange={(e) => setF({ ...f, difference_reason: e.target.value })} className="mt-3" data-testid="recon-diff-reason" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-stone-900 text-white" onClick={submit} data-testid="recon-save"><Calculator size={13} className="mr-1" />Reconcile</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ═══════════ Monthly Closing ═══════════ */
function ClosingPanel({ accounts }) {
    const [month, setMonth] = useState(currentMonth());
    const [selected, setSelected] = useState("");
    const [closings, setClosings] = useState([]);
    const [dialog, setDialog] = useState(false);

    useEffect(() => { if (!selected && accounts[0]) setSelected(accounts[0].id); }, [accounts, selected]);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/cash/months");
            setClosings(r.data?.closings || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (payload) => {
        try {
            const res = await api.post("/cash/months/close", payload);
            toast.success(res.data.status === "Closed" ? "Month closed cleanly" : `Closed with difference ${fmtMoney(res.data.difference)}`);
            setDialog(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[160px]" data-testid="close-month-picker" />
                    <Select value={selected} onValueChange={setSelected}>
                        <SelectTrigger className="w-[220px]" data-testid="close-account"><SelectValue placeholder="Pick account" /></SelectTrigger>
                        <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-sm text-stone-500">Lock the month per account. Closing balance carries forward.</span>
                </div>
                <Button size="sm" onClick={() => setDialog(true)} disabled={!selected} data-testid="close-new"><Lock size={13} className="mr-1" /> Close month</Button>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="closings-table">
                <table className="dense-table w-full">
                    <thead>
                        <tr><th>Month</th><th>Account</th><th className="text-right">Opening</th><th className="text-right">Cash in</th><th className="text-right">Cash out</th><th className="text-right">Expected</th><th className="text-right">Physical</th><th className="text-right">Diff</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {closings.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-stone-400 py-8">No months closed yet.</td></tr>
                        ) : closings.map((c) => (
                            <tr key={c.id} data-testid={`close-row-${c.id}`}>
                                <td className="tabular-nums">{c.month}</td>
                                <td>{c.account_name}</td>
                                <td className="text-right tabular-nums">{fmtMoney(c.opening_balance)}</td>
                                <td className="text-right tabular-nums text-emerald-700">{fmtMoney(c.cash_in)}</td>
                                <td className="text-right tabular-nums text-rose-700">{fmtMoney(c.cash_out)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(c.expected_balance)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(c.physical_cash)}</td>
                                <td className={`text-right tabular-nums font-medium ${Math.abs(c.difference || 0) < 0.01 ? "text-emerald-700" : "text-rose-700"}`}>{fmtMoney(c.difference)}</td>
                                <td><span className={`text-[11px] px-2 py-0.5 rounded-full ${c.status === "Closed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{c.status}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {dialog && (
                <MonthCloseDialog
                    accountId={selected}
                    accountName={accounts.find((a) => a.id === selected)?.name}
                    month={month}
                    onClose={() => setDialog(false)}
                    onSubmit={submit}
                />
            )}
        </>
    );
}

function MonthCloseDialog({ accountId, accountName, month, onClose, onSubmit }) {
    const [physical, setPhysical] = useState("");
    const [remarks, setRemarks] = useState("");
    const submit = () => {
        if (physical === "" || Number(physical) < 0) { toast.error("Enter physical cash count"); return; }
        onSubmit({ account_id: accountId, month, physical_cash: Number(physical), remarks });
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="month-close-dialog">
                <DialogHeader><DialogTitle>Close {month} · {accountName}</DialogTitle></DialogHeader>
                <div className="text-sm text-stone-500 mb-3">
                    <AlertCircle className="inline mr-1" size={13} />
                    Ensure all weekly reconciliations for this month are done. This action locks the month for this account.
                </div>
                <div className="grid grid-cols-1 gap-3">
                    <div><Label className="eyebrow">Physical cash on hand at month end (₹)</Label>
                        <Input type="number" value={physical} onChange={(e) => setPhysical(e.target.value)} data-testid="close-physical" />
                    </div>
                    <div><Label className="eyebrow">Remarks</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="bg-stone-900 text-white" onClick={submit} data-testid="close-save"><Lock size={13} className="mr-1" />Lock month</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ═══════════ Reports ═══════════ */
function ReportsPanel() {
    const [month, setMonth] = useState(currentMonth());
    const [report, setReport] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/cash/reports/monthly", { params: { month } });
            setReport(r.data);
        } catch (err) { toast.error(formatApiError(err)); }
    }, [month]);
    useEffect(() => { load(); }, [load]);

    return (
        <>
            <div className="flex items-center gap-2 mb-3">
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[160px]" data-testid="report-month" />
                <span className="text-sm text-stone-500">Account-wise cash movement for the selected month.</span>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-4" data-testid="report-table">
                <table className="dense-table w-full">
                    <thead>
                        <tr><th>Account</th><th>Type</th><th className="text-right">Opening</th><th className="text-right">Cash in</th><th className="text-right">Cash out</th><th className="text-right">Closing</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                        {!report?.accounts?.length ? (
                            <tr><td colSpan={7} className="text-center text-stone-400 py-8">No data.</td></tr>
                        ) : report.accounts.map((a) => (
                            <tr key={a.account_id} data-testid={`report-row-${a.account_id}`}>
                                <td className="font-medium">{a.account_name}</td>
                                <td>{a.account_type}</td>
                                <td className="text-right tabular-nums">{fmtMoney(a.opening_balance)}</td>
                                <td className="text-right tabular-nums text-emerald-700">{fmtMoney(a.cash_in)}</td>
                                <td className="text-right tabular-nums text-rose-700">{fmtMoney(a.cash_out)}</td>
                                <td className="text-right tabular-nums font-semibold">{fmtMoney(a.closing_balance)}</td>
                                <td><span className={`text-[11px] px-2 py-0.5 rounded-full ${a.closed ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>{a.closed_status}</span></td>
                            </tr>
                        ))}
                    </tbody>
                    {report?.totals && (
                        <tfoot>
                            <tr className="font-semibold border-t-2 border-stone-200">
                                <td colSpan={3}>Totals</td>
                                <td className="text-right tabular-nums text-emerald-700">{fmtMoney(report.totals.cash_in)}</td>
                                <td className="text-right tabular-nums text-rose-700">{fmtMoney(report.totals.cash_out)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(report.totals.closing_balance)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
            {report?.type_breakdown?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-stone-100 text-sm font-semibold text-stone-700">Movement by type</div>
                    <table className="dense-table w-full">
                        <thead><tr><th>Type</th><th className="text-right">Count</th><th className="text-right">Cash in</th><th className="text-right">Cash out</th></tr></thead>
                        <tbody>
                            {report.type_breakdown.map((t) => (
                                <tr key={t.type}>
                                    <td className="capitalize">{t.type.replace("_", " ")}</td>
                                    <td className="text-right tabular-nums">{t.count}</td>
                                    <td className="text-right tabular-nums text-emerald-700">{fmtMoney(t.cash_in)}</td>
                                    <td className="text-right tabular-nums text-rose-700">{fmtMoney(t.cash_out)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
